import { Show } from '@refinedev/antd';
import { useShow, type BaseRecord } from '@refinedev/core';
import { Card, Descriptions, Space, Table } from 'antd';
import { Json, renderValue } from '../render';
import type { ResourceConfig } from '../resources';

const isRows = ( value: unknown ): value is BaseRecord[] => Array.isArray( value ) && value.length > 0 && value.every( ( v ) => v !== null && typeof v === 'object' && ! Array.isArray( v ) );
const isObject = ( value: unknown ): value is Record<string, unknown> => value !== null && typeof value === 'object' && ! Array.isArray( value );

/**
 * Every field of a record: scalars as a description list, lists of rows
 * (fetches, participants, schedule …) as tables, objects (payloads, cursors,
 * details) as JSON.
 */
export function ResourceShow( { config }: { config: ResourceConfig } ) {
	const { query, result } = useShow<BaseRecord>( { resource: config.name } );
	const record = result ?? {};
	const entries = Object.entries( record );
	const scalars = entries.filter( ( [ , value ] ) => ! isRows( value ) && ! isObject( value ) && ! ( Array.isArray( value ) && value.some( isObject ) ) );
	const tables = entries.filter( ( [ , value ] ) => isRows( value ) );
	const objects = entries.filter( ( [ key, value ] ) => isObject( value ) || ( Array.isArray( value ) && ! isRows( value ) && value.some( isObject ) ) || ( Array.isArray( value ) && value.length === 0 && key in ( config.nested ?? {} ) ) );

	return (
		<Show isLoading={ query.isLoading } title={ `${ config.label }: ${ String( record.id ?? '' ) }` } canEdit={ false } canDelete={ false }>
			<Space direction="vertical" size="large" style={ { width: '100%' } }>
				<Descriptions bordered size="small" column={ { xs: 1, md: 2 } }>
					{ scalars.map( ( [ key, value ] ) => (
						<Descriptions.Item key={ key } label={ key.replace( /_/g, ' ' ) }>
							{ key === 'id' ? String( value ) : renderValue( key, value ) }
						</Descriptions.Item>
					) ) }
				</Descriptions>
				{ tables.map( ( [ key, rows ] ) => (
					<Card key={ key } size="small" title={ `${ key.replace( /_/g, ' ' ) } (${ ( rows as BaseRecord[] ).length })` }>
						<Table dataSource={ rows as BaseRecord[] } rowKey={ ( row ) => String( row.id ?? JSON.stringify( row ) ) } size="small" pagination={ { pageSize: 25, hideOnSinglePage: true } } scroll={ { x: true } }>
							{ Object.keys( ( rows as BaseRecord[] )[ 0 ] ?? {} ).map( ( column ) => (
								<Table.Column<BaseRecord> key={ column } dataIndex={ column } title={ column.replace( /_/g, ' ' ) } render={ ( value ) => renderValue( column, value, column === 'id' ? config.nested?.[ key ] : undefined ) } />
							) ) }
						</Table>
					</Card>
				) ) }
				{ objects.map( ( [ key, value ] ) => (
					<Card key={ key } size="small" title={ key.replace( /_/g, ' ' ) }>
						<Json value={ value } />
					</Card>
				) ) }
			</Space>
		</Show>
	);
}
